import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation, IButtonProps } from 'core/components';
import { usePartBomItemActions } from 'partBoms/hooks';
import { IPartBomItem } from 'partBoms/types';
import { PartNumberHelper } from 'parts/helpers';
import { Color, Variant } from 'theme/enums';

interface IProps extends IButtonProps {
  items: IPartBomItem[];
  buttonElement?: ReactElement;
  onRemoveSuccess?: () => void;
}

export const RemoveItemsButton = ({ items, buttonElement, onRemoveSuccess, ...rest }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removePartBomItems } = usePartBomItemActions();

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const { success } = await removePartBomItems({
      ids: items.map((item) => item.id),
    });

    if (!success) {
      return;
    }

    setTimeout(() => {
      onRemoveSuccess?.();
    }, 500);
  }, [removePartBomItems, items, onRemoveSuccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={rest?.text || t('buttonLabelRemove')}
        variant={Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        {...rest}
      />
    );
  }, [buttonElement, t, rest]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePartBomItems', {
          count: items.length,
        })}
        message={t('messageConfirmRemovePartBomItems', {
          count: items.length,
          number: PartNumberHelper.toLabel(items[0].partArr?.number),
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
